import { gql } from '@apollo/client';
import { announcementBannerFragment } from '../src/components/AnnouncementBanner';
import { BLOG_POSTS_PAGINATION_PAGE_SIZE, NEWS_POSTS_PAGINATION_PAGE_SIZE, NEWS_POSTS_PER_CATEGORY_ON_INDEX } from '../src/Config';
import { comparisonMatrixModule } from '../src/modules/ComparisonMatrix';
import { customerLogosModule } from '../src/modules/CustomerLogos';
import { faqModule } from '../src/modules/FAQ';
import { featureCardsModule } from '../src/modules/FeatureCards';
import { greenhouseModule } from '../src/modules/GreenHouse';
import { headingModule } from '../src/modules/Heading';
import { hubspotFormModule } from '../src/modules/HubspotForm';
import { largeQuoteModule } from '../src/modules/LargeQuote';
import { latestUpdatesModule } from '../src/modules/LatestUpdates';
import { mainHeroModule } from '../src/modules/MainHero';
import { pageBottomCtaModule } from '../src/modules/PageBottomCTA';
import { pageLinksModule } from '../src/modules/PageLinks';
import { pricingTableModule } from '../src/modules/PricingTable';
import { simpleQuoteModule } from '../src/modules/SimpleQuote';
import { subpageHeader } from '../src/modules/SubpageHeader';
import { CMSInterface, request } from './CMSInterface';
import {
  ResourceLibraryItem, WistiaOEmbedData,
} from './types';

const collectionRegex = /[a-z]*Collection$/;

const productionToken = process.env.CONTENTFUL_KEY as string;
const previewToken = process.env.CONTENTFUL_PREVIEW_KEY as string;
const url = `https://graphql.contentful.com/content/v1/spaces/${process.env.CONTENTFUL_SPACE_ID!}`;
const envEnvironment = process.env.CONTENTFUL_ENVIRONMENT_OVERRIDE || process.env.CONTENTFUL_ENVIRONMENT || 'master';

function generateContentUrl(environment: string) {
  if (environment !== '') {
    return `${url}/environments/${environment}`;
  }

  return url;
}

/**
 * This function takes parsed JSON from Contentful
 * and transforms it into a flatter structure.
 *
 * It iterates through the tree structure and looks for object keys
 * that ends with the string "Collection". When it finds such a key
 * it takes its inner child "items" and flattens the object using the
 * string that precedes "Collection" as the new key and the value of
 * the inner key as the new value.
 *
 * We ignore the eslint rules for TypeScript here since this
 * function can not be written in a type safe manner.
 */
function normalizeContentfulResult(obj: any): any {
  /* eslint-disable @typescript-eslint/no-unsafe-argument */
  /* eslint-disable @typescript-eslint/no-unsafe-assignment */
  /* eslint-disable @typescript-eslint/no-unsafe-member-access */
  /* eslint-disable @typescript-eslint/no-unsafe-call */
  /* eslint-disable @typescript-eslint/no-unsafe-return */
  if (Array.isArray(obj)) {
    return obj.map(normalizeContentfulResult);
  }
  if (typeof obj === 'object' && obj !== null) {
    const result = new Map();
    Object.entries(obj).forEach((arr: any) => {
      const [key, value] = arr;
      if (collectionRegex.test(key)) {
        result.set(key.slice(0, key.length - 'Collection'.length), normalizeContentfulResult(value.items));
      } else {
        result.set(key, normalizeContentfulResult(value));
      }
    });
    return Object.fromEntries(result);
  }
  return obj;
}

export const blogPostModule = gql`
${pageBottomCtaModule}
fragment blogPostModule on BlogPost {
  title
  slug
  categories
  backgroundImage {...image}
  featureImage {...image}
  date
  excerpt
  metaDescription
  author {
    name
    role
    description
    profilePicture {
      ...image
    }
  }
  content {
    json
    links {
      entries {
        block {
          sys { id }
          __typename
          ...button
          ...link
          ... on WistiaVideo {
            videoId
            type
          }
          ... on ContentAlignment {
            alignment
            content {
              json
            }
          }
        }
        inline {
          sys { id }
          __typename
          ...link
          ...button
        }
      }
      assets {
        block {
          size
          url
          title
          sys { id }
        }
      }
    }
  }
  bottomCtaModule {
    ...pageBottomCtaModule
  }
}
`;

// Moved here because lodash uses eval, which breaks the middleware
export const cardsModule = gql`
fragment cardsModule on CardsModule {
  intunioModuleTypeId
  cardsHeading
  headingSize
  paddingTop
  paddingBottom
  theme
  link {...link}
  cardsCollection(limit: 32) {
    items {
      sys { id }
      heading
      body {
        json
      }
      imagePosition
      largeIcon
      image {...image}
      link {...link}
    }
  }
  anchorId
}`;

// Moved here because dotlottie player uses eval, which breaks the middleware
export const contentModule = gql`
fragment contentModule on ContentModule {
  intunioModuleTypeId
  mediaBeforeContent
  paddingTop
  paddingBottom
  roundedCorners
  mediaPadding
  singleColumn
  buttonPrimary {...link}
  buttonSecondary {...link}
  theme
  content {
    json
    links {
      assets {
        block {
          sys { id }
          title
          fileName
        }
      }
    }
  }
  image {...image}
  backgroundImage {...image}
  callToActionBottomLink {...link}
  wistiaVideo { videoId type }
  anchorId
}`;

const moduleFragment = gql`
${mainHeroModule}
${contentModule}
${simpleQuoteModule}
${cardsModule}
${headingModule}
${subpageHeader}
${customerLogosModule}
${largeQuoteModule}
${hubspotFormModule}
${latestUpdatesModule}
${faqModule}
${pageLinksModule}
${pricingTableModule}
${greenhouseModule}
${featureCardsModule}
${pageBottomCtaModule}
${comparisonMatrixModule}
fragment module on PageModulesItem {
  ...mainHeroModule
  ...contentModule
  ...simpleQuoteModule
  ...cardsModule
  ...headingModule
  ...subpageHeaderModule
  ...customerLogosModule
  ...pageBottomCtaModule
  ...largeQuoteModule
  ...hubspotFormModule
  ...latestUpdatesModule
  ...faqModule
  ...pageLinksModule
  ...pricingTableModule
  ...greenhouseModule
  ...featureCardsModule
  ...comparisonMatrixModule
}
fragment moduleCollection on PageModulesCollection {
  items {
    ...module
  }
}
`;

const popularPosts = gql`
fragment popularPosts on BlogPagePopularPostsCollection {
  items {
    sys { id }
    slug
    author {
      name
    }
    title
    categories
    content { json }
    featureImage {...image}
    date
  }
}`;

const getPageByIdQuery = gql`
${moduleFragment}
${announcementBannerFragment}
  query getPageById($id: String!, $preview: Boolean) {
    page(id: $id, preview: $preview) {
      title
      slug
      metaDescription
      modulesCollection(limit: 32) {...moduleCollection}
      announcementBanner {
        ...announcementBannerFragment
      }
    }
  }
`;

const getCareerListingPageQuery = gql`
${moduleFragment}
query getCareerListingPage {
  careerJobPostingPageCollection(limit: 1) {
    items {
      title
      metaDescription
      backgroundImage {
        title
        url
      }
      modulesCollection(limit: 32) {
        items { ...module }
      }
    }
  }
}`;

const getBlogPostBySlug = gql`
${blogPostModule}
${popularPosts}
${pageLinksModule}
query getBlogPostBySlugQuery($slug: String!) {
  blogPostCollection(where: {slug: $slug}) {
    items {
      ...blogPostModule
    }
  }
  blogPageCollection(limit: 1) {
    items {
      # Get the first 4 blog posts, then in js filter out the one we are currently on
      popularPostsCollection(limit: 4) {
        ...popularPosts
      }
      bottomPageLinks {
        ...pageLinksModule
      }
    }
  }
}`;

const getBlogPostWithId = gql`
${blogPostModule}
${popularPosts}
${pageLinksModule}
query getBlogPostByIdQuery($id: String!, $preview: Boolean) {
  blogPost(id: $id, preview: $preview) {
      ...blogPostModule
  }
  blogPageCollection(limit: 1) {
    items {
      # Get the first 4 blog posts, then in js filter out the one we are currently on
      popularPostsCollection(limit: 4) {
        ...popularPosts
      }
      bottomCtaModule {
        ...pageBottomCtaModule
      }
      bottomPageLinks {
        ...pageLinksModule
      }
    }
  }
}`;

const getBlogPostsPaginatedQuery = gql`
query getBlogPostsPaginatedQuery($limit: Int!, $skip: Int! $filter: BlogPostFilter) {
  # If categories is empty, we want to return all blog posts, otherwise we want to filter by category
  blogPostCollection(limit: $limit, skip: $skip, order: date_DESC, where: $filter) {
      total
      items {
        sys {
        id
      }
      date
      slug
      categories
      content { json }
      title
      excerpt
      featureImage {...image}
      author {
        name
      }
    }
  }
}`;

const getBlogPostsPaths = gql`
query getBlogPostsPaths {
  blogPostCollection {
    items {
      slug
    }
  }
}`;

// Get all news posts paths which has no link set
const getNewsPostsPaths = gql`
query getNewsPostsPaths {
      newsPostCollection(where: { link_exists: false }) {
        items {
          slug
          type
        }
      }
    }
`;

const getSummarizedBlogPostsQuery = gql`
query getSummarizedBlogPostsQuery($filter: BlogPostFilter) {
  blogPostCollection(where: $filter) {
    total
    items {
      categories
    }
  }

  allPostCollection: blogPostCollection {
    total
    items {
      categories
    }
  }
}
`;

const getBlogArchivePage = gql`
${pageBottomCtaModule}
${popularPosts}
${pageLinksModule}
query getBlogArchivePage {
  blogPageCollection(limit: 1) {
    items {
      title
      metaDescription
      sys { id }
      bottomCtaModule {
        ...pageBottomCtaModule
      }
      blogLogo {...image}
      backgroundImage {...image}
      featuredPost {
        sys { id }
        slug
        categories
        title
        featureImage {...image}
        date
        featurePostExcerpt
      }
      popularPostsCollection(limit: 3) {
        ...popularPosts
      }

      bottomPageLinks {
        ...pageLinksModule
      }

      emailFormTitle
      emailFormDescription
      emailFormSubmitButtonText
    }
  }
}
`;

const navbarsFragment = gql`
fragment navigationItemPart on NavigationItem {
  title
  icon {...image}
  link {...link}
}
fragment navigationItem on NavigationItem {
  ...navigationItemPart
  childrenCollection(limit: 3) {
    items {
      ...navigationItemPart
      childrenCollection(limit: 10) {
        items {
          ...navigationItemPart
        }
      }
    }
  }
}`;
const menuEntryQuery = gql`
${navbarsFragment}
query menuEntryQuery {
  menu(id: "1FR8lMqMrDItbkbTv5hleN") {
    title
    itemsCollection(limit: 8) {
      items {
        ... on NavigationItem {
          ...navigationItem
        }
      }
    }
    secondaryItemsCollection(limit: 4) {
      items {
        ... on NavigationItem {
          ...navigationItem
        }
      }
    }
    callToAction {...link}
    copyrightSuffix
    address
    termsLinksCollection {
      items {...link}
    }
  }
}`;

const staticPathsQuery = gql`
query getStaticPaths($preview: Boolean!) {
  pageCollection(preview: $preview) {
    items {
      sys {
        id
      }
      slug
    }
  }
}
`;

const getRedirectsQuery = gql`
  query getRedirects {
    redirectCollection {
      items {
        from
        to
        permanent
      }
    }
  }
`;
const getResourceLibraryCount = gql`
  query getResourceLibraryCount($types: [String!], $query: String) {
    resourceLibraryItemCollection(where: { type_in: $types, OR: [
      { title_contains: $query },
      { description_contains: $query }
    ] }) {
      total
    }

    allResourceLibraryItemCollection: resourceLibraryItemCollection {
      total
      items {
        type
      }
    }
  }
`;

const getResourceLibraryTypes = gql`
query getResourceLibraryTypes { resourceLibraryItemCollection(order: type_ASC, limit: 11000) { items { type } } }`;

const getResourceLibraryItemsQuery = gql`
  query getResourceLibraryItems($limit: Int!, $skip: Int!, $types: [String]!, $query: String) {
    resourceLibraryItemCollection(limit: $limit, skip: $skip, where: { type_in: $types, OR: [
      { title_contains: $query },
      { description_contains: $query }
     ]}, order: date_DESC) {
      items {
        sys { id }
        title
        type
        image {...image}
        description
        linkedResource {...link}
        assetResource { url }
        wistiaVideoId
        thumbnailPosition
      }
    }
  }
`;

const getResourceLibraryPage = gql`
${moduleFragment}
query getResourceLibrary {
  resourceLibraryCollection(limit: 1) {
    items {
      title
      metaDescription
      ogImage { ...image }
      topModulesCollection(limit: 32) {
        items { ...module }
      }
      theme
      bottomModulesCollection(limit: 32) {
        items { ...module }
      }
    }
  }
}
`;

const getNewsCount = gql`
query getNewsCount($types: [String!]) {
  newsPostCollection(where: { type_in: $types }) {
    total
  }
}
`;

const newsItem = gql`
fragment newsItem on NewsPost {
    sys { id }
    title
    slug
    type
    date
    endDate
    location
    image {...image}
    link {...link}
  }
`;

const fullNewsItemQuery = gql`
${pageBottomCtaModule}
fragment fullNewsItem on NewsPost  {
    sys { id }
    title
    slug
    type
    date
    endDate
    location
    image {...image}
    backgroundImage {...image}
    link {...link}
    metaDescription
    bottomCtaModule { ...pageBottomCtaModule }
    content {
      json
      links {
        entries {
          block {
            sys { id }
            __typename
            ...button
            ...link
            ... on WistiaVideo {
              videoId
              type
            }
          }
          inline {
            sys { id }
            __typename
            ...link
            ...button
          }
        }
        assets {
          block {
            size
            url
            title
            sys { id }
          }
        }
      }
    }
  }
`;

const getNewsPage = gql`
${moduleFragment}
${newsItem}
  query getNewsPage {
    newsPageCollection(limit: 1) {
      items {
        title
        metaDescription
        backgroundImage {...image}
        featuredAll {...newsItem}
        featuredEvent {...newsItem}
        featuredNews {...newsItem}
        featuredPressRelease {...newsItem}
        bottomModulesCollection(limit: 32) {
          items { ...module }
        }
      }
    }
  }
`;

const getNewsPosts = gql`
${newsItem}
  query getNewsPostsQuery($types: [String!], $skip: Int!, $limit: Int!) {
    newsPostCollection(limit: $limit, skip: $skip, where: { type_in: $types }, order: date_DESC) {
      items {...newsItem}
    }
  }
`;

const getNewsPostBySlug = gql`
${fullNewsItemQuery}
  query getNewsPostBySlugQuery($type: String!, $slug: String!) {
    newsPostCollection(limit: 1, where: {type: $type, slug: $slug}) {
      items {...fullNewsItem}
    }
  }
`;

const getNewsPostById = gql`
${fullNewsItemQuery}
query getNewsPostByIdQuery($id: String!, $preview: Boolean) {
  newsPost(id: $id, preview: $preview) {
    ...fullNewsItem
  }
}`;

const getSearchFiltersFromQuery = (search: string) => ({
  OR: [
    { title_contains: search },
    { content_contains: search },
  ],
});

const Contentful: CMSInterface = {
  getWelcomePage: async () => {
    const result = await request({
      query: staticPathsQuery,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: { preview: false },
    });
    const normalized = normalizeContentfulResult(result);
    const page = normalized.page.find((p: any) => p.slug === '/');
    if (!page) {
      throw new Error("Found no page with slug '/'");
    }
    return normalizeContentfulResult(await request({
      query: getPageByIdQuery,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: { id: page.sys.id, preview: false },
    }));
  },
  getNavbar: async () => {
    const result = await request({
      query: menuEntryQuery,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
    });
    const normalized = normalizeContentfulResult(result);
    return normalized;
  },
  getStaticPaths: async () => {
    const result = await request({
      query: staticPathsQuery,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: { preview: false },
    });
    const normalized = normalizeContentfulResult(result);
    return normalized.page.filter((p: any) => p.slug !== '/');
  },
  getRedirects: async () => {
    const result = await request({
      query: getRedirectsQuery,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
    });
    const normalized = normalizeContentfulResult(result);
    return normalized.redirect;
  },
  getPageBySlug: async (slug: string) => {
    const result = await request({
      query: staticPathsQuery,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: { preview: false },
    });
    const normalized = normalizeContentfulResult(result);
    const page = normalized.page.find((p: any) => p.slug === `/${slug}`);
    return normalizeContentfulResult(await request({
      query: getPageByIdQuery,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: { id: page.sys.id },
    }));
  },
  getPageById: async (id: string, preview: boolean, environment = '') => {
    const token = preview ? previewToken : productionToken;
    return normalizeContentfulResult(await request({
      query: getPageByIdQuery,
      url: generateContentUrl(environment || envEnvironment),
      token,
      variables: { id, preview },
    }));
  },
  getTotalNumberOfBlogPosts: async (selectedCategories: string[], search: string = '') => {
    const result: {
        blogPostCollection: { total: number, items: { categories?: string[] }[] },
        allPostCollection: { total: number, items: {categories?: string[] }[] },
      } = await request({
        query: getSummarizedBlogPostsQuery,
        url: generateContentUrl(envEnvironment),
        token: productionToken,
        variables: { filter: getSearchFiltersFromQuery(search) },
      });

    const categoryCounts: {[category: string]: number} = {};

    const allPostCollection = result.allPostCollection?.items || [];

    allPostCollection.forEach(({ categories }) => {
      categories?.forEach((category) => {
        const oldCount = categoryCounts[category] || 0;
        categoryCounts[category] = oldCount + 1;
      });
    });

    const numberOfPosts: number = (selectedCategories.reduce((acc, category) => {
      if (category in categoryCounts) {
        return acc + categoryCounts[category];
      }
      return acc;
    }, 0) || result?.blogPostCollection?.total || 0);

    return { numberOfPosts, categories: categoryCounts };
  },
  async getPaginatedBlogPostsEntries(page: number, categories: string[], search: string = '') {
    const skipMultiplier = page === 1 ? 0 : page - 1;
    const skip = skipMultiplier > 0 ? BLOG_POSTS_PAGINATION_PAGE_SIZE * skipMultiplier : 0;
    const categoriesFilter = categories.length ? { categories_contains_some: categories } : {};
    return (normalizeContentfulResult(await request({
      query: getBlogPostsPaginatedQuery,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: {
        skip,
        categories: await this.getWhitelistedCategories(categories),
        limit: BLOG_POSTS_PAGINATION_PAGE_SIZE,
        filter: {
          AND: [categoriesFilter, getSearchFiltersFromQuery(search)],
        },
      },
    })));
  },
  getBlogPageArchive: async () => {
    const data = normalizeContentfulResult(await request({
      query: getBlogArchivePage,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
    }));
    return data.blogPage[0];
  },
  getBlogPostsPaths: async () => {
    const data = normalizeContentfulResult(await request({
      query: getBlogPostsPaths,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
    }));
    return data.blogPost;
  },
  getBlogPostWithSlug: async (slug: string, preview: boolean) => {
    const token = preview ? previewToken : productionToken;
    const data = normalizeContentfulResult(await request({
      query: getBlogPostBySlug,
      url: generateContentUrl(envEnvironment),
      token,
      variables: { slug },
    }));
    return [data.blogPost[0], data.blogPage[0].popularPosts, data.blogPage[0].bottomPageLinks];
  },
  getBlogPostWithId: async (id: string, preview: boolean) => {
    const token = preview ? previewToken : productionToken;
    const data = normalizeContentfulResult(await request({
      query: getBlogPostWithId,
      url: generateContentUrl(envEnvironment),
      token,
      variables: { id, preview },
    }));
    return [data.blogPost, data.blogPage[0].popularPosts, data.blogPage[0].bottomPageLinks];
  },
  async getWhitelistedCategories(categories: string[]) {
    const availableCategories = Object.entries(
      (await this.getTotalNumberOfBlogPosts(categories)).categories,
    )
      .map(([c]) => c);
    let whiteListedCategories = categories
      .filter((category) => availableCategories.includes(category));
    if (whiteListedCategories.length === 0) {
      whiteListedCategories = [];
    }

    return whiteListedCategories;
  },
  getCareerListingPage: async () => {
    const data = normalizeContentfulResult(await request({
      query: getCareerListingPageQuery,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
    }));
    if (data?.careerJobPostingPage.length > 0) {
      return data.careerJobPostingPage[0];
    }
    return data;
  },
  async getWhitelistedTypes(types: string[]) {
    const availableTypes = await this.getResourceLibraryTypes();

    let whiteListedTypes = types.filter((type) => availableTypes.includes(type));
    if (whiteListedTypes.length === 0) {
      whiteListedTypes = availableTypes;
    }

    return whiteListedTypes;
  },
  async getResourceCounts(types: string[], query: string) {
    const result: {
      resourceLibraryItemCollection: { total: number }, allResourceLibraryItemCollection: {
        items: { type: string }[]
    } } = await request({
      query: getResourceLibraryCount,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: { types: await this.getWhitelistedTypes(types), query },
    });

    const { total } = result.resourceLibraryItemCollection;

    const typeCounts: Record<string, number> = {};
    const allPostCollection = result.allResourceLibraryItemCollection.items || [];

    allPostCollection.forEach(({ type }) => {
      const oldCount = typeCounts[type] || 0;
      typeCounts[type] = oldCount + 1;
    });

    return {
      total,
      typeCounts,
    };
  },
  async getResourceLibraryItems(
    skip: number,
    perPage: number,
    types: string[],
    query: string,
  ) {
    const data = normalizeContentfulResult(await request({
      query: getResourceLibraryItemsQuery,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: {
        types: await this.getWhitelistedTypes(types),
        skip: skip * perPage,
        limit: perPage,
        query: query || undefined,
      },
    }));
    const newData = await Promise.all(
      (data.resourceLibraryItem as ResourceLibraryItem[]).map(async (resource) => {
        const newResource = resource;
        if (resource.wistiaVideoId) {
          try {
            const wistiaDataResp = await fetch(`https://fast.wistia.com/oembed?url=https://bluescape.wistia.com/medias/${resource.wistiaVideoId}`);
            if (wistiaDataResp.ok) {
              newResource.wistiaData = await wistiaDataResp.json() as WistiaOEmbedData;
            }
          } catch (e) {
          // eslint-disable-next-line no-console
            console.error(e);
          }
        }
        return newResource;
      }),
    );
    return newData;
  },
  async getResourceLibraryTypes() {
    const result: { resourceLibraryItemCollection: { items: {type: string}[] } } = await request({
      query: getResourceLibraryTypes,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
    });

    return Array.from(
      new Set(result?.resourceLibraryItemCollection?.items.map((item) => item.type)),
    );
  },
  async getResourceLibraryPage() {
    const data = normalizeContentfulResult(await request({
      query: getResourceLibraryPage,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
    }));
    return data.resourceLibrary[0];
  },
  async getNewsPage() {
    const data = normalizeContentfulResult(await request({
      query: getNewsPage,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
    }));
    return data.newsPage[0];
  },
  async getTotalNumberOfNews(type) {
    const data = await request<any>({
      query: getNewsCount,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: { types: type ? [type] : ['news', 'press-release', 'event'] },
    });

    return data?.newsPostCollection?.total;
  },
  async getNewsPosts(type, page, customLimit) {
    const perPage = (type ? NEWS_POSTS_PAGINATION_PAGE_SIZE : NEWS_POSTS_PER_CATEGORY_ON_INDEX);
    const skip = perPage * page;
    const data = normalizeContentfulResult(await request({
      query: getNewsPosts,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: {
        types: type ? [type] : ['news', 'press-release', 'event'],
        skip,
        limit: customLimit || perPage,
      },
    }));
    return data?.newsPost;
  },
  async getNewsPostsPaths() {
    const data = normalizeContentfulResult(await request({
      query: getNewsPostsPaths,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
    }));
    return data?.newsPost;
  },
  async getNewsPostBySlug(type, slug) {
    const data = normalizeContentfulResult(await request({
      query: getNewsPostBySlug,
      url: generateContentUrl(envEnvironment),
      token: productionToken,
      variables: { type, slug },
    }));
    return data?.newsPost?.[0];
  },
  async getNewsPostById(id, preview) {
    const data = normalizeContentfulResult(await request({
      query: getNewsPostById,
      url: generateContentUrl(envEnvironment),
      token: preview ? previewToken : productionToken,
      variables: { id, preview },
    }));

    return data?.newsPost;
  },
};

export default Contentful;
